import { useState, type FormEvent, type ReactNode } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
  signOut,
  GoogleAuthProvider,
  type AuthError,
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { auth } from '../config/firebase';
import { api, ApiError } from '../lib/api';

type AuthMode = 'login' | 'signup';

// ── Signup data persisted in localStorage so the verify-email page can use it ──

const SIGNUP_DATA_KEY = 'iomi_signup_data';

interface SignupData {
  email: string;
  name: string;
  tempPassword: string;
}

export function getSignupData(): SignupData | null {
  try {
    const raw = localStorage.getItem(SIGNUP_DATA_KEY);
    return raw ? (JSON.parse(raw) as SignupData) : null;
  } catch {
    return null;
  }
}

export function clearSignupData(): void {
  localStorage.removeItem(SIGNUP_DATA_KEY);
}

// ── Pending-signup flag: prevents auth flash while creating + signing out ──

const PENDING_SIGNUP_KEY = 'iomi_pending_signup';

export function isPendingSignup(): boolean {
  return sessionStorage.getItem(PENDING_SIGNUP_KEY) === 'true';
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function generateTempPassword(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  let password = '';
  const array = new Uint8Array(24);
  crypto.getRandomValues(array);
  for (let i = 0; i < 24; i++) {
    password += chars[array[i] % chars.length];
  }
  return password;
}

function parseAuthError(error: AuthError): string {
  switch (error.code) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/weak-password':
      return 'Password must be at least 8 characters and contain a letter and a number.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

// ── Email pre-check ─────────────────────────────────────────────────────────

interface EmailCheckResult {
  exists: boolean;
  providers: string[];
  emailVerified?: boolean;
}

async function checkEmailAccount(email: string): Promise<EmailCheckResult | null> {
  try {
    const res = await api.post<{ success: boolean; data: EmailCheckResult }>(
      '/auth/check-email',
      { email },
    );
    return res.data;
  } catch {
    // If the check fails (e.g. backend unreachable), return null to fall through
    // to the normal Firebase flow.
    return null;
  }
}

function providerLabel(providerId: string): string {
  switch (providerId) {
    case 'google.com': return 'Google';
    case 'apple.com': return 'Apple';
    case 'microsoft.com': return 'Microsoft';
    case 'facebook.com': return 'Facebook';
    default: return providerId;
  }
}

function getRedirectPath(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get('redirect') || '/';
}

// ── Shared input class ──────────────────────────────────────────────────────
const inputClass =
  'w-full rounded-lg border border-stone bg-white px-4 py-3 text-sm ' +
  'text-midnight placeholder:text-slate outline-none ' +
  'focus:border-plum focus:ring-2 focus:ring-plum/20 transition-all duration-150';

// ── Forgot-password sub-view ────────────────────────────────────────────────

function ForgotPasswordView({
  email,
  setEmail,
  onBack,
  onSwitchToSignup,
}: {
  email: string;
  setEmail: (v: string) => void;
  onBack: () => void;
  onSwitchToSignup: () => void;
}) {
  const [error, setError] = useState<ReactNode | null>(null);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // Check if the account exists and what providers it uses
      const check = await checkEmailAccount(email);

      if (check && !check.exists) {
        setError(
          <span>
            No account found for this email.{' '}
            <button
              type="button"
              onClick={onSwitchToSignup}
              className="text-plum font-semibold hover:underline"
            >
              Sign up instead
            </button>
          </span>,
        );
        return;
      }

      if (check && check.exists && !check.providers.includes('password')) {
        const names = check.providers.map(providerLabel).join(', ');
        setError(
          <span>
            This account uses <span className="font-semibold">{names}</span> sign-in
            and doesn't have a password to reset.{' '}
            <button
              type="button"
              onClick={onBack}
              className="text-plum font-semibold hover:underline"
            >
              Go back to log in
            </button>{' '}
            with {names}.
          </span>,
        );
        return;
      }

      await api.post('/auth/send-password-reset', { email });
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Failed to send password reset email. Please try again.');
      } else {
        setError(parseAuthError(err as AuthError));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Back link */}
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-slate hover:text-midnight
                   transition-colors self-start -mb-2"
      >
        <ChevronLeft size={16} />
        Back to Log In
      </button>

      <div>
        <h2 className="font-display text-h2 font-semibold text-midnight">
          Reset Password
        </h2>
        <p className="text-sm text-slate mt-1 leading-relaxed">
          Enter your email and we'll send a link to reset your password.
        </p>
      </div>

      {sent ? (
        <div className="flex flex-col gap-5">
          <div className="px-4 py-4 rounded-xl bg-green-50 border border-green-200
                          text-sm text-green-700 leading-relaxed">
            Reset link sent to <span className="font-semibold">{email}</span>.
            Check your inbox.
          </div>
          <button type="button" onClick={onBack} className="btn-primary w-full">
            Back to Log In
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="reset-email" className="field-label">Email</label>
            <input
              id="reset-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={inputClass}
            />
          </div>

          {error && (
            <div role="alert" className="text-sm text-red-600">{error}</div>
          )}

          <button type="submit" disabled={submitting} className="btn-primary w-full">
            {submitting ? 'Checking...' : 'Send Reset Link'}
          </button>
        </form>
      )}
    </div>
  );
}

// ── Signup-email-sent confirmation sub-view ─────────────────────────────────

function SignupEmailSentView({
  email,
  onBack,
}: {
  email: string;
  onBack: () => void;
}) {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  const [debugLog, setDebugLog] = useState<string[]>([]);

  const initialError = sessionStorage.getItem('iomi_signup_email_error');
  const hadInitialFailure = !!initialError;

  function addDebugLine(line: string) {
    setDebugLog(prev => [...prev, `${new Date().toLocaleTimeString()} — ${line}`]);
  }

  async function handleResend() {
    setResending(true);
    setResendError(null);
    addDebugLine(`Resending verification email to ${email}...`);
    try {
      const res = await api.post<{ success: boolean; data?: { skipped?: boolean; reason?: string } }>(
        '/auth/send-verification-email',
        { email },
      );
      addDebugLine(`Server responded: ${JSON.stringify(res)}`);
      setResent(true);
      sessionStorage.removeItem('iomi_signup_email_error');
    } catch (err) {
      const msg = err instanceof ApiError
        ? err.message
        : err instanceof Error ? err.message : 'Unknown error';
      const details = err instanceof ApiError ? JSON.stringify(err.data) : '';
      addDebugLine(`Failed: ${msg}${details ? ` | data: ${details}` : ''}`);
      setResendError(msg);
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="font-display text-h2 font-semibold text-midnight">
          Check Your Email
        </h2>
        <p className="text-sm text-slate mt-1 leading-relaxed">
          {hadInitialFailure && !resent
            ? <>We had trouble sending the verification email to{' '}
                <span className="font-semibold text-midnight">{email}</span>.
                Please try resending it below.</>
            : <>We sent a verification link to{' '}
                <span className="font-semibold text-midnight">{email}</span>.</>
          }
        </p>
      </div>

      {hadInitialFailure && !resent ? (
        <div className="px-4 py-4 rounded-xl bg-amber-50 border border-amber-200
                        text-sm text-amber-700 leading-relaxed">
          The verification email may not have been delivered. Tap &ldquo;Resend&rdquo; below to try again.
          {initialError && (
            <p className="mt-2 text-[12px] text-amber-600 font-mono break-all">
              Error: {initialError}
            </p>
          )}
        </div>
      ) : (
        <div className="px-4 py-4 rounded-xl bg-green-50 border border-green-200
                        text-sm text-green-700 leading-relaxed">
          Click the link in the email to verify your address and finish setting up
          your account.
        </div>
      )}

      {resendError && (
        <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200
                        text-sm text-red-600 leading-relaxed">
          {resendError}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={handleResend}
          disabled={resending || resent}
          className="text-sm text-plum font-semibold hover:underline disabled:opacity-50 self-start"
        >
          {resent ? 'Email resent ✓' : resending ? 'Resending...' : "Didn't get the email? Resend"}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="btn-secondary w-full text-sm"
        >
          Back to Log In
        </button>
      </div>

      {/* Debug panel */}
      <div className="border-t border-stone/40 pt-3">
        <button
          type="button"
          onClick={() => setShowDebug(v => !v)}
          className="text-[11px] text-slate hover:text-midnight transition-colors"
        >
          {showDebug ? 'Hide diagnostics' : 'Show diagnostics'}
        </button>
        {showDebug && (
          <div className="mt-2 bg-stone/10 rounded-lg p-3 text-[11px] font-mono text-slate space-y-1 max-h-48 overflow-y-auto">
            <p>Email: {email}</p>
            <p>Initial send: {hadInitialFailure ? `FAILED — ${initialError}` : 'OK'}</p>
            <p>Resend status: {resent ? 'Success' : resendError ? `Failed — ${resendError}` : 'Not attempted'}</p>
            {debugLog.length > 0 && (
              <>
                <p className="pt-1 border-t border-stone/30 font-semibold">Activity log:</p>
                {debugLog.map((line, i) => <p key={i}>{line}</p>)}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Incomplete account notice (shown on signup when email is already in use) ─

function IncompleteAccountNotice({
  email,
  onSwitchToLogin,
}: {
  email: string;
  onSwitchToLogin: () => void;
}) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleResetPassword() {
    setSending(true);
    try {
      await api.post('/auth/send-password-reset', { email });
      setSent(true);
    } catch {
      // If it fails, fall through — the user can still use "Log In" to try forgot-password
      setSent(true);
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="flex flex-col gap-3">
        <div className="px-4 py-3 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700 leading-relaxed">
          Password reset link sent to <span className="font-semibold">{email}</span>.
          Check your inbox to set a new password and finish setting up your account.
        </div>
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-plum text-sm font-semibold hover:underline self-start"
        >
          Go to Log In
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800 leading-relaxed">
        An account with this email already exists but hasn't finished setup.
        Reset your password to complete your account.
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleResetPassword}
          disabled={sending}
          className="text-sm font-semibold text-white bg-plum px-4 py-2 rounded-lg
                     hover:bg-rose active:opacity-80 disabled:opacity-50
                     transition-colors duration-150"
        >
          {sending ? 'Sending…' : 'Reset Password'}
        </button>
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-plum text-sm font-semibold hover:underline"
        >
          Log In instead
        </button>
      </div>
    </div>
  );
}

// ── Main AuthPage ───────────────────────────────────────────────────────────

export function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<AuthMode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ReactNode | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Persisted in sessionStorage so it survives the brief mount/unmount cycle
  // caused by the auth state flash during signup.
  const [signupEmailSent, setSignupEmailSent] = useState<string | null>(() =>
    sessionStorage.getItem('iomi_signup_email'),
  );

  function switchMode(next: AuthMode) {
    setMode(next);
    setError(null);
    setPassword('');
    setShowForgotPassword(false);
  }

  // ── Login submit ────────────────────────────────────────────────────────
  async function handleLoginSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // Pre-check whether the account exists and which providers it uses
      const check = await checkEmailAccount(email);

      if (check && !check.exists) {
        setError(
          <span>
            No account found for this email.{' '}
            <button
              type="button"
              onClick={() => switchMode('signup')}
              className="text-plum font-semibold hover:underline"
            >
              Sign up instead
            </button>
          </span>,
        );
        return;
      }

      if (check && check.exists && !check.providers.includes('password')) {
        const names = check.providers.map(providerLabel).join(', ');
        setError(
          <span>
            This account uses <span className="font-semibold">{names}</span> sign-in.
            Use the &quot;Sign in with Google&quot; button below instead.
          </span>,
        );
        return;
      }

      await signInWithEmailAndPassword(auth, email, password);

      // Redirect through the router so its location updates. A raw
      // history.replaceState() leaves React Router on /login, which has no
      // route once signed in and renders a blank page.
      navigate(getRedirectPath(), { replace: true });
    } catch (err) {
      setError(parseAuthError(err as AuthError));
    } finally {
      setSubmitting(false);
    }
  }

  // ── Signup submit ───────────────────────────────────────────────────────
  async function handleSignupSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    // Set flag so AppRoutes doesn't flash the authenticated view
    sessionStorage.setItem(PENDING_SIGNUP_KEY, 'true');

    try {
      const tempPassword = generateTempPassword();

      // Create the account (signs the user in)
      const cred = await createUserWithEmailAndPassword(auth, email, tempPassword);

      // Set display name so the verification email includes it
      await updateProfile(cred.user, { displayName: name.trim() });

      // Persist signup data for the verify-email page
      localStorage.setItem(
        SIGNUP_DATA_KEY,
        JSON.stringify({ email, name: name.trim(), tempPassword }),
      );

      // If this email has a pending org invitation, auto-verify via Admin SDK
      // so the user can skip the email verification step.
      let autoVerified = false;
      try {
        const verifyRes = await api.post<{ success: boolean; data: { verified: boolean } }>(
          '/auth/verify-invited-email',
          { email, uid: cred.user.uid },
        );
        autoVerified = verifyRes.data.verified === true;
      } catch {
        // If the check fails, fall through to normal verification flow
      }

      if (autoVerified) {
        // Email auto-verified — skip verification email, go straight to password form
        await signOut(auth);
        window.location.href = '/verify-email';
        return;
      }

      // Brief delay to let Firebase propagate the newly created user record
      // before calling generateEmailVerificationLink via the backend.
      await new Promise(r => setTimeout(r, 1500));

      // Send verification email via backend (Resend template instead of Firebase default)
      let emailSendError: string | null = null;
      try {
        await api.post('/auth/send-verification-email', { email });
      } catch (emailErr) {
        // Log but don't block signup — the account is already created.
        console.error('[Signup] Failed to send verification email:', emailErr);
        emailSendError = emailErr instanceof ApiError
          ? emailErr.message
          : emailErr instanceof Error ? emailErr.message : 'Unknown error';
      }

      // Sign out — the user must verify their email before proceeding
      await signOut(auth);

      // Show confirmation view (persisted in sessionStorage in case of re-mount)
      sessionStorage.setItem('iomi_signup_email', email);
      if (emailSendError) {
        sessionStorage.setItem('iomi_signup_email_error', emailSendError);
      }
      setSignupEmailSent(email);
    } catch (err) {
      const authErr = err as AuthError;

      // If the email is already in use, check if the account was never fully set up
      if (authErr.code === 'auth/email-already-in-use') {
        const check = await checkEmailAccount(email);

        if (check?.exists) {
          // Google-only account — tell them to sign in with Google
          if (!check.providers.includes('password')) {
            const names = check.providers.map(providerLabel).join(', ');
            setError(
              <span>
                An account with this email already exists using{' '}
                <span className="font-semibold">{names}</span> sign-in.
                Use the &quot;Sign up with Google&quot; button below instead.
              </span>,
            );
          } else {
            // Password account exists — show incomplete-account helper
            setError(
              <IncompleteAccountNotice
                email={email}
                onSwitchToLogin={() => switchMode('login')}
              />,
            );
          }
        } else {
          setError(parseAuthError(authErr));
        }
      } else {
        setError(parseAuthError(authErr));
      }
    } finally {
      sessionStorage.removeItem(PENDING_SIGNUP_KEY);
      setSubmitting(false);
    }
  }

  // ── Google auth ─────────────────────────────────────────────────────────
  async function handleGoogle() {
    setError(null);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      // Redirect through the router so its location updates. A raw
      // history.replaceState() leaves React Router on /login, which has no
      // route once signed in and renders a blank page.
      navigate(getRedirectPath(), { replace: true });
    } catch (err) {
      const authErr = err as AuthError;
      if (
        authErr.code !== 'auth/popup-closed-by-user' &&
        authErr.code !== 'auth/cancelled-popup-request'
      ) {
        setError(parseAuthError(authErr));
      }
    }
  }

  function handleBackFromSignupSent() {
    sessionStorage.removeItem('iomi_signup_email');
    sessionStorage.removeItem('iomi_signup_email_error');
    setSignupEmailSent(null);
    switchMode('login');
  }

  return (
    <div className="min-h-screen bg-mist flex flex-col items-center justify-start pt-12 px-5 pb-12">
      <div className="w-full max-w-sm flex flex-col gap-6">

        {/* ── Hero ────────────────────────────────────── */}
        <div className="text-center px-2 pt-4 pb-2">
          <h1 className="font-display text-display text-midnight leading-tight">
            Let's Get{' '}
            <span className="font-extrabold text-plum">Moving</span>
          </h1>
          <p className="text-small text-slate mt-3 leading-relaxed">
            Run your moving company from anywhere — your truck, your desk, or your pocket.
          </p>
        </div>

        {/* ── Form card ──────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-brand-md border border-stone/60 px-5 py-6">
          {signupEmailSent ? (
            <SignupEmailSentView
              email={signupEmailSent}
              onBack={handleBackFromSignupSent}
            />
          ) : showForgotPassword ? (
            <ForgotPasswordView
              email={email}
              setEmail={setEmail}
              onBack={() => { setShowForgotPassword(false); setError(null); }}
              onSwitchToSignup={() => { setShowForgotPassword(false); switchMode('signup'); }}
            />
          ) : (
            <div className="flex flex-col gap-5">

              {/* Mode toggle */}
              <div className="flex bg-mist rounded-lg p-1 gap-1">
                {(['login', 'signup'] as AuthMode[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => switchMode(m)}
                    className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150
                      ${mode === m
                        ? 'bg-white text-midnight shadow-brand-sm'
                        : 'text-slate hover:text-midnight'
                      }`}
                  >
                    {m === 'login' ? 'Log In' : 'Sign Up'}
                  </button>
                ))}
              </div>

              {/* Google auth — primary option */}
              <button
                type="button"
                onClick={handleGoogle}
                className="btn-secondary w-full flex items-center justify-center gap-3"
              >
                <GoogleIcon />
                {mode === 'login' ? 'Sign in with Google' : 'Sign up with Google'}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-stone" />
                <span className="text-[13px] text-slate select-none">or</span>
                <div className="flex-1 h-px bg-stone" />
              </div>

              {/* ── Login form ──────────────────────────── */}
              {mode === 'login' && (
                <form onSubmit={handleLoginSubmit} className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="email" className="field-label">Email</label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className={inputClass}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="password" className="field-label">Password</label>
                    <input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className={inputClass}
                    />
                  </div>

                  <div className="flex justify-end -mt-1">
                    <button
                      type="button"
                      onClick={() => { setError(null); setShowForgotPassword(true); }}
                      className="text-[13px] text-plum font-semibold hover:underline"
                    >
                      Forgot my password
                    </button>
                  </div>

                  {error && (
                    <div role="alert" className="text-sm text-red-600">{error}</div>
                  )}

                  <button type="submit" disabled={submitting} className="btn-primary w-full">
                    {submitting ? 'Checking...' : 'Log In'}
                  </button>
                </form>
              )}

              {/* ── Signup form (name + email only) ─────── */}
              {mode === 'signup' && (
                <form onSubmit={handleSignupSubmit} className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="signup-name" className="field-label">Name</label>
                    <input
                      id="signup-name"
                      type="text"
                      autoComplete="name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your full name"
                      className={inputClass}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="signup-email" className="field-label">Email</label>
                    <input
                      id="signup-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className={inputClass}
                    />
                  </div>

                  {error && (
                    <div role="alert" className="text-sm text-red-600">{error}</div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting || !name.trim() || !email}
                    className="btn-primary w-full"
                  >
                    {submitting ? 'Sending verification...' : 'Create Account'}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>

        {/* ── Footer note ────────────────────────────── */}
        <p className="text-center text-[13px] text-slate leading-relaxed px-2">
          Commercial estimates made fast and accurate, powered by the industry
          leading IOMI methodology. Learn how IOMI can improve your commercial
          moving business at{' '}
          <a
            href="https://officemoves.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-plum font-semibold hover:underline"
          >
            officemoves.com
          </a>
        </p>

      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}
